import { FieldValues } from "react-hook-form";
import moment, { Moment } from "moment-jalaali";
import { Option } from "components/dynamic/DynamicForm";
import { Filters } from "types/globalTypes";

// build query params
interface BuildQueryParamsArgs {
  page?: number;
  limit?: number;
  filters?: Filters;
}

export const buildQueryParams = ({
  page,
  limit,
  filters = {},
}: BuildQueryParamsArgs): string => {
  const params = new URLSearchParams();

  Object.entries(filters)?.forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      (!Array.isArray(value) || value.length > 0)
    ) {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v.toString()));
      } else {
        params.append(key, value.toString());
      }
    }
  });

  if (page !== undefined) {
    params.append("page", page.toString());
  }
  if (limit !== undefined) {
    params.append("limit", limit.toString());
  }

  return params.toString();
};

// format time
export const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// format date/time
type FormData = Record<
  string,
  string | number | Date | Moment | null | undefined
>;

export const formatDateTime = (
  form: FormData,
  fields: FieldValues[]
): FormData => {
  const formatted: FormData = { ...form };

  fields.forEach(({ name, type }) => {
    const val = form[name];
    if (val === null || val === undefined) return;

    let dateObj: Moment;

    if (val instanceof Date) {
      dateObj = moment(val);
    } else if (typeof val === "string" || typeof val === "number") {
      dateObj = moment(val);
    } else if (moment.isMoment(val)) {
      dateObj = val;
    } else {
      return;
    }

    if (!dateObj.isValid()) return;

    if (type === "date") {
      formatted[name] = dateObj.format("YYYY-MM-DD");
    } else if (type === "time") {
      formatted[name] = dateObj.format("HH:mm");
    }
  });

  return formatted;
};

// trim string
export const trimStrings = <T extends Record<string, unknown>>(data: T): T => {
  const trimmed: Partial<T> = {};
  for (const key in data) {
    const val = data[key];
    if (typeof val === "string") {
      const str = val.trim();
      const boolValue = parseBoolean(str);
      if (boolValue !== null) {
        trimmed[key] = boolValue as T[typeof key];
      } else {
        trimmed[key] = (str === "" ? null : str) as T[typeof key];
      }
    } else if (Array.isArray(val)) {
      trimmed[key] = val.map((v) => {
        if (typeof v === "string") {
          const str = v.trim();
          const boolValue = parseBoolean(str);
          if (boolValue !== null) {
            return boolValue;
          }
          return str === "" ? null : str;
        }
        return v;
      }) as T[typeof key];
    } else {
      trimmed[key] = val;
    }
  }
  return trimmed as T;
};

const parseBoolean = (value: string): boolean | null => {
  const lowerValue = value.toLowerCase().trim();
  if (lowerValue === "true") return true;
  if (lowerValue === "false") return false;
  return null;
};

// enum options
export function enumToOptions<T extends Record<string, string | number>>(
  enumObj: T,
  t: (key: string) => string,
  prefix: string
): Option[] {
  return Object.values(enumObj).map((value) => ({
    value: value as string | number,
    label: t(`${prefix}.${value}`),
  }));
}

// progress companies
export const calculateStepProgress = (
  currentStep: number,
  totalSteps: number
) => {
  return Math.round(((currentStep - 1) / (totalSteps - 1)) * 100);
};

// phone number tel
export function normalizePhone(value: string) {
  return value.replace(/\s+/g, "");
}

export function formatLandline(value: string) {
  const digits = value.replace(/\D/g, "");

  return digits
    .replace(/^(\d{3})(\d{0,4})(\d{0,4})$/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(" ")
    )
    .trim();
}
